import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { ApiError } from '../api/client';
import { authApi } from '../api/auth';
import type { LoginRequest, User } from '../api/types';

const ME_KEY = ['auth', 'me'] as const;

/**
 * useCurrentUser — query for /auth/me. Returns:
 *   - user: User | null   (null when unauthenticated)
 *   - isLoading           (true on first load; false after error or success)
 *   - error: unknown      (non-401 errors only)
 */
export function useCurrentUser() {
  const q = useQuery<User | null, ApiError>({
    queryKey: ME_KEY,
    queryFn: async () => {
      try {
        return await authApi.me();
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) {
          return null;        // unauthenticated is a normal state, not an error
        }
        throw e;
      }
    },
    staleTime: 60_000,
  });
  return {
    user: q.data ?? null,
    isLoading: q.isLoading,
    isAuthenticated: !!q.data,
    error: q.error,
    refetch: q.refetch,
  };
}

/**
 * useLogin — mutation that authenticates and then invalidates the /auth/me query
 * so the rest of the app sees the new identity.
 */
export function useLogin() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  return useMutation<User, ApiError, LoginRequest>({
    mutationFn: authApi.login,
    onSuccess: (user) => {
      qc.setQueryData(ME_KEY, user);
      navigate('/', { replace: true });
    },
  });
}

/**
 * useLogout — clears server session and local query cache, navigates to /login.
 */
export function useLogout() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  return useMutation<void, ApiError>({
    mutationFn: authApi.logout,
    onSettled: () => {
      qc.setQueryData(ME_KEY, null);
      qc.clear();
      navigate('/login', { replace: true });
    },
  });
}
